// @flow
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import LinkButton from '@justpark/ui/src/components/LinkButton';
import LegacyLink from '../../Common/LegacyLink';
import Zendesk from '../../Common/ZendeskLink';
import { selectIsAppFeatureLimited } from '../../../stores/partner';
import { logout } from '../../../stores/auth';

type Props = {
  secondaryClass: string,
  isAdmin: boolean,
  user: ?any,
  isStatic: boolean,
  hideFindParking: boolean,
  mobileVisible: boolean,
  secondaryVisible: boolean,
  toggleSecondary: () => void,
  toggleMobileMenu: () => void
};

const SecondaryNav = ({
  secondaryClass,
  isAdmin,
  user,
  isStatic,
  hideFindParking,
  mobileVisible,
  secondaryVisible,
  toggleSecondary,
  toggleMobileMenu
}: Props) => {
  const isSpaceOwner = user?.isSpaceOwner;
  const hasSeasonTickets = user?.hasSeasonTicket;
  const isAppFeatureLimited = useSelector(selectIsAppFeatureLimited);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const locale = i18n.language;
  const isWelsh = locale === 'cy';

  const onClick = (e, isLegacy) => {
    if (isLegacy) {
      return;
    }

    if (secondaryVisible) {
      toggleSecondary(e);
    }
  };

  const unreadMessages = user?.unreadMessages;

  return (
    <ul className={`${secondaryClass} c-header__menu--secondary`}>
      {isAdmin && !isWelsh && (
        <>
          <li onClick={(e) => onClick(e, true)} key="su">
            <LegacyLink to="admin/su/">SU</LegacyLink>
          </li>
          <li onClick={(e) => onClick(e, true)} key="admin">
            <LegacyLink to="admin/">Admin</LegacyLink>
          </li>
        </>
      )}

      {!hideFindParking && !isWelsh && (
        <li onClick={onClick} className="desktop-hidden" data-cy="find-parking">
          <Link onClick={onClick} to="/">
            {t('navigation:findParking', 'Find parking')}
          </Link>
        </li>
      )}

      {!user && !isAppFeatureLimited && !isWelsh && (
        <>
          <li
            onClick={(e) => onClick(e, true)}
            className="desktop-hidden"
            data-cy="h-company"
          >
            <LegacyLink to="company/overview/">Company</LegacyLink>
          </li>
          <li
            onClick={(e) => onClick(e, true)}
            className="desktop-hidden"
            data-cy="how-it-works"
          >
            <LegacyLink to="how-it-works/overview/">How it works</LegacyLink>
          </li>
          <li
            onClick={(e) => onClick(e, true)}
            className="desktop-hidden"
            data-cy="h-business"
          >
            <LegacyLink to="business/overview/">Business</LegacyLink>
          </li>
        </>
      )}

      {user && (
        <>
          <li onClick={onClick} key="bookings-made">
            <Link to="/dashboard/bookings/made">
              {t('navigation:bookingsMade', 'Bookings made')}
            </Link>
          </li>

          {isSpaceOwner && !isAppFeatureLimited && (
            <li onClick={onClick} key="bookings-received">
              <Link to="/dashboard/bookings/received">
                {t('navigation:bookingsReceived', 'Bookings received')}
              </Link>
            </li>
          )}

          {hasSeasonTickets && !isAppFeatureLimited && (
            <li onClick={onClick} key="season-tickets">
              <Link to="/dashboard/season-tickets">
                {t('navigation:seasonTickets', 'Tickets & permits')}
              </Link>
            </li>
          )}

          {!isAppFeatureLimited && (
            <li onClick={(e) => onClick(e, true)} key="messages">
              <LegacyLink to="dashboard/messaging/">
                {unreadMessages === 0 && (
                  <Trans i18nKey="navigation:messages">Messages</Trans>
                )}
                {unreadMessages > 0 && (
                  <Trans i18nKey="navigation:messagesWithAmount">
                    Messages <div>{{ unreadMessages }}</div>
                  </Trans>
                )}
              </LegacyLink>
            </li>
          )}

          {isSpaceOwner && !isAppFeatureLimited && (
            <li onClick={onClick}>
              <Link to="/dashboard/parking-spaces">
                {t('navigation:parkingSpaces', 'Manage my spaces')}
              </Link>
            </li>
          )}

          {!isAppFeatureLimited && (
            <li onClick={(e) => onClick(e, true)}>
              <LegacyLink to="dashboard/billing/">
                {t('navigation:billing', 'Billing & withdrawals')}
              </LegacyLink>
            </li>
          )}

          <li onClick={onClick}>
            <Link to="/dashboard/profile/">
              {t('navigation:profile', 'My profile')}
            </Link>
          </li>
        </>
      )}

      {!isSpaceOwner && !isAppFeatureLimited && !isWelsh && (
        <li
          onClick={(e) => onClick(e, true)}
          key="rentoutdriveway"
          className="desktop-hidden"
          data-cy="rent-your-space"
        >
          <LegacyLink to="how-it-works/rent-out-your-driveway/">
            {t('navigation:rentOutDriveway', 'Rent out your space')}
          </LegacyLink>
        </li>
      )}

      {!isAppFeatureLimited && !isWelsh && (
        <>
          <li
            onClick={onClick}
            key="downloadapp"
            className="desktop-hidden"
            data-cy="h-download"
          >
            <a href="https://justpark.onelink.me/3963458492?c=website-homepage&af_dp=justpark://download-app">
              {t('navigation:downloadApp', 'Download our app')}
            </a>
          </li>
          <li
            onClick={(e) => onClick(e, true)}
            className="desktop-hidden"
            data-cy="h-ev"
          >
            <LegacyLink to="ev/ev-home/">{t('navigation:ev', 'EV')}</LegacyLink>
          </li>
        </>
      )}

      <li
        onClick={onClick}
        key="help"
        className="desktop-hidden"
        data-cy="h-help"
      >
        {isAppFeatureLimited || isWelsh ? (
          <Link to="/help">{t('cta:help', 'Help')}</Link>
        ) : (
          <Zendesk isStatic={isStatic}>{t('cta:help', 'Help')}</Zendesk>
        )}
      </li>

      {user && (
        <li onClick={onClick} key="logout">
          <LinkButton
            type="button"
            onClick={() => {
              dispatch(logout());
              if (mobileVisible) {
                toggleMobileMenu();
              }
            }}
          >
            {t('cta:logOut', 'Log out')}
          </LinkButton>
        </li>
      )}

      {!user && (
        <li onClick={onClick} className="desktop-hidden">
          <Link to="/auth/login">
            {t('navigation:signUpLogin', 'Sign up / login')}
          </Link>
        </li>
      )}
    </ul>
  );
};

export default SecondaryNav;
